"use client";

import type { ReactNode } from "react";

import { ChevronRightIcon, MapIcon } from "../../dx-icons";
import { MainVisual, MainVisualSkeleton } from "../../main-visual/main-visual";
import {
  AccordionSection,
  AccordionSectionSkeleton,
} from "../../ui/accordion-section";

import { AmenitiesContent } from "./amenities-content";
import { CtaButton, type CtaAction } from "./cta-button";
import { NotesBanner } from "./notes-banner";
import { SpecList, SpecListSkeleton } from "./spec-list";
import { TitleArea, TitleAreaSkeleton } from "./title-area";

/**
 * 部屋詳細モーダルの描画に必要な正規化済みデータ（plain DTO）= このコンポーネントの入力契約。
 *
 * Accommodation 形（部屋番号あり）と RoomType 形（部屋番号なし）の両方を、境界の
 * mapper (`to-room-detail-view`) がこのシェイプに揃えて渡す。`RoomDetailContent` は
 * この DTO だけを描画し、どちらの GraphQL シェイプから来たかを意識しない。
 */
export type RoomDetailView = {
  heroImages: { url: string }[];
  /** Hero 画像の alt。大タイトルを流用する */
  altText: string;
  /** 大タイトル（拠点名 / 建築タイプ名 / (あれば)部屋番号） */
  bigTitle: string;
  /** 小タイトル（ルームタイプ displayName）。未設定なら null */
  subtitle: string | null;
  /** 注意書き。未設定なら null */
  notes: string | null;
  /** 収容人数の表示文字列。未設定なら null */
  capacityDescription: string | null;
  /** 広さの表示文字列。未設定なら null */
  sizeDescription: string | null;
  /** 室内設備の表示文字列。該当なしなら null */
  indoorEquipment: string | null;
  features: ReadonlyArray<RoomDetailFeature>;
  /** 備品リストの外部 URL。未設定なら null */
  equipmentsListUrl: string | null;
  /** Google Maps の URL。未設定なら null */
  googleMapsUrl: string | null;
};

type RoomDetailFeature = {
  slug: string;
  displayName: string;
  iconSlug?: string | null;
  sort: number;
};

export type RoomDetailContentProps = {
  /**
   * 正規化済みの部屋詳細データ。Accommodation 形 / RoomType 形のどちらから来ても
   * 境界の mapper で `RoomDetailView` に揃えて渡す（このコンポーネントは
   * GraphQL シェイプを意識しない）。
   */
  view: RoomDetailView;
  /**
   * 末尾に描画する CTA。`kind` で「予約する」/「閉じる」を切り替え。
   * 未指定なら CTA を出さない。`inlineContentSlot` がある場合はそちらを優先する。
   */
  primaryAction?: CtaAction;
  /**
   * roomType 経路の予約カレンダー等。本体スクロール領域内に常時描画するため sticky にしない。
   * 指定時は `primaryAction` より優先する。
   */
  inlineContentSlot?: ReactNode;
};

export function RoomDetailContent({
  view,
  primaryAction,
  inlineContentSlot,
}: RoomDetailContentProps) {
  return (
    <div className="bg-dx-surface-base flex flex-col">
      <MainVisual images={view.heroImages} altText={view.altText} />

      <TitleArea bigTitle={view.bigTitle} subtitle={view.subtitle} />

      {view.notes && <NotesBanner notes={view.notes} />}

      <SpecList
        capacity={view.capacityDescription}
        roomSize={view.sizeDescription}
        indoorEquipment={view.indoorEquipment}
        features={view.features}
      />

      <SectionDivider />
      <AccordionSection title="備品リスト" defaultOpen={true}>
        <AmenitiesContent
          equipmentsListUrl={
            view.equipmentsListUrl ??
            "https://2ndhome.sa-nu.com/supplies_list_new/"
          }
        />
      </AccordionSection>

      {view.googleMapsUrl && (
        <>
          <SectionDivider />
          <AccessLink url={view.googleMapsUrl} />
        </>
      )}

      {inlineContentSlot ??
        (primaryAction && <CtaButton action={primaryAction} />)}
    </div>
  );
}

function SectionDivider() {
  return <div className="bg-dx-border-default h-px" />;
}

/**
 * 「アクセス」セクション。Google Maps へ飛ぶ外部リンク。
 */
function AccessLink({ url }: { url: string }) {
  return (
    <a
      data-analytics-id="room_detail_clicked_access_map"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-dx-surface-base flex items-center py-[6px] pr-[8px] pl-[16px]"
    >
      <span className="font-notojp text-dx-base-black flex-1 py-[10px] text-[20px] leading-[1.4] font-semibold">
        アクセス
      </span>
      <span className="text-dx-text-default flex size-[48px] shrink-0 items-center justify-center">
        <MapIcon />
      </span>
      <span className="text-dx-text-default flex size-[48px] shrink-0 items-center justify-center">
        <ChevronRightIcon />
      </span>
    </a>
  );
}

function AccessLinkSkeleton() {
  return (
    <div className="bg-dx-surface-base flex items-center py-[6px] pr-[8px] pl-[16px]">
      <div className="flex-1 py-[10px]">
        <div className="bg-dx-surface-subtle h-[24px] w-[30%] animate-pulse rounded-sm" />
      </div>
      <div className="flex size-[48px] shrink-0 items-center justify-center">
        <div className="bg-dx-surface-subtle size-[20px] animate-pulse rounded-sm" />
      </div>
      <div className="flex size-[48px] shrink-0 items-center justify-center">
        <div className="bg-dx-surface-subtle size-[20px] animate-pulse rounded-sm" />
      </div>
    </div>
  );
}

/**
 * ロード中の placeholder。`RoomDetailContent` と同じセクション構成で並べることで、
 * ロード完了時の Drawer 高さに寄せレイアウトシフトを抑える。
 * 各 sub-component が自身の Skeleton を export しており、ここで compose するだけ。
 */
export function RoomDetailContentSkeleton() {
  return (
    <div
      className="bg-dx-surface-base flex flex-col"
      role="status"
      aria-busy="true"
      aria-label="部屋情報を取得中"
    >
      <MainVisualSkeleton />
      <TitleAreaSkeleton />
      <SpecListSkeleton />
      <SectionDivider />
      <AccordionSectionSkeleton />
      <SectionDivider />
      <AccessLinkSkeleton />
    </div>
  );
}
